import { inject, ChangeDetectorRef } from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { ErrorDisplayMode } from "@models/interfaces/configuration/DynamicForm.interface";

export abstract class BaseControl<T = unknown> implements ControlValueAccessor {
  protected readonly cdr = inject(ChangeDetectorRef);
  protected control = inject(NgControl, { optional: true, self: true });
  protected value!: T | null;
  protected errorDisplayMode: ErrorDisplayMode | undefined = ErrorDisplayMode.Always;

  constructor() {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  protected onChange!: (...args: unknown[]) => void | undefined;

  public onTouched(): void {
    this.cdr.detectChanges();
  }

  public registerOnChange(fn: (...args: unknown[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public writeValue(value: T | null): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  protected onValueChange(value: T): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  protected get hasErrors(): boolean {
    return this.control?.errors && this.shouldShowErrors() || false;
  }

  protected shouldShowErrors(): boolean {
    if (!this.control) return false;

    switch (this.errorDisplayMode) {
      case ErrorDisplayMode.Always:
        return true;
      case ErrorDisplayMode.Dirty:
        return this.control.dirty || true;
      case ErrorDisplayMode.Touched:
        return this.control.touched || false;
      default:
        return false;
    }
  }
}